import React, { useState, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { Table, Space, Button, Tag, message, Tabs, Tooltip } from "antd";
import { ISender, IAdvert } from "../../interfaces";
import { useHashNavigation } from "../../hooks/useHashNavigation";
import { EditOutlined, DeleteOutlined, PlusOutlined, UserOutlined, HomeOutlined, EyeOutlined, CalendarOutlined, DollarOutlined, CheckSquareOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;

export const SenderList: React.FC = () => {
  const { navigateWithHash } = useHashNavigation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("adverts");
  const [adverts, setAdverts] = useState<IAdvert[]>([]);
  const [loading, setLoading] = useState(false);
  const { tableProps } = useTable<ISender>();

  useEffect(() => {
    // Lade Adverts von der API statt aus der statischen JSON
    const loadAdverts = async () => {
      setLoading(true);
      try {
        const apiUrl = `${window.location.origin}/?type=8847379247&tx_buchungsmanager_frontend[controller]=RestApi&tx_buchungsmanager_frontend[action]=getAllMyAdverts`;
        const response = await fetch(apiUrl, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdverts(data.adverts || []);
        } else {
          console.error('Fehler beim Laden der Adverts:', response.statusText);
          message.error('Fehler beim Laden der Inserate');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Adverts:', error);
        message.error('Fehler beim Laden der Inserate');
      } finally {
        setLoading(false);
      }
    };

    loadAdverts();
  }, []);

  const handleEdit = (record: ISender) => {
    // Da wir noch keine Edit-Seite für Absender haben, zeigen wir eine Info
    message.info(`Bearbeiten von Absender "${record.company || record.name}" wird implementiert...`);
  };

  const handleDelete = (record: ISender) => {
    // Lösch-Logik
    message.success(`Absender "${record.company || record.name}" wurde gelöscht`);
  };

  const handleAdvertDetail = (record: IAdvert) => {
    navigate(`/objekte/${record.uid}`);
  };

  return (
    <List 
      title="Objekte / Inserate verwalten"
      headerButtons={[
        <Button
          key="create"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            if (activeTab === 'adverts') {
              navigate('/objekte/neu');
            } else {
              message.info('Neuen Eigentümer erstellen wird implementiert...');
            }
          }}
        >
          {activeTab === 'adverts' ? 'Neues Inserat' : 'Neuer Eigentümer'}
        </Button>,
      ]}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Tab für Inserate (Adverts) */}
        <TabPane 
          tab={
            <span>
              <HomeOutlined />
              Inserate
            </span>
          } 
          key="adverts"
        >
          <Table 
            dataSource={adverts} 
            rowKey="uid"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1400 }}
          >
            <Table.Column
              dataIndex="uid"
              title="ID"
              width={80}
              sorter
            />
            <Table.Column
              dataIndex="title"
              title="Titel"
              width={180}
              render={(value, record: IAdvert) => (
                <Space direction="vertical" size={0}>
                  <strong>{value || "Ohne Titel"}</strong>
                  {record.shorttitle && (
                    <small style={{ color: '#999' }}>{record.shorttitle}</small>
                  )}
                </Space>
              )}
            />
            <Table.Column
              title="Adresse"
              width={150}
              render={(_, record: IAdvert) => (
                <Space direction="vertical" size={0}>
                  <span>{record.street} {record.houseNumber}</span>
                  <small style={{ color: '#999' }}>
                    {record.zip} {record.city}
                  </small>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="status"
              title="Status"
              width={100}
              render={(value) => (
                <Tag color={value === 1 ? 'green' : 'red'}>
                  {value === 1 ? 'Aktiv' : 'Inaktiv'}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="star"
              title="Sterne"
              width={100}
              render={(value) => (
                <span>{"⭐".repeat(value || 0)}</span>
              )}
            />
            <Table.Column
              dataIndex="noPersons"
              title="Max. Gäste"
              width={100}
            />
            <Table.Column
              title="Preis"
              width={120}
              render={(_, record: IAdvert) => (
                <span>
                  {record.price?.forOnePerson ? `ab ${record.price.forOnePerson} €` : '-'}
                </span>
              )}
            />
            <Table.Column
              title="An-/Abreise-Regeln"
              width={150}
              render={(_, record: IAdvert) => {
                const rules = record.overview_data?.arrival_departure_rule_labels || [];
                if (rules.length === 0) {
                  return <Tag color="default">Keine Regeln</Tag>;
                }
                return (
                  <Space direction="vertical" size={2}>
                    {rules.slice(0, 2).map((rule: any) => (
                      <Tag key={rule.id} color="blue" icon={<CalendarOutlined />}>
                        {rule.label}
                      </Tag>
                    ))}
                    {rules.length > 2 && (
                      <Tooltip title={rules.slice(2).map((r: any) => r.label).join(', ')}>
                        <Tag color="blue">+{rules.length - 2} weitere</Tag>
                      </Tooltip>
                    )}
                  </Space>
                );
              }}
            />
            <Table.Column
              title="Preiskonfiguration"
              width={140}
              render={(_, record: IAdvert) => {
                const pricingStatus = record.overview_data?.pricing_status;
                if (!pricingStatus) {
                  return <Tag color="red">Unbekannt</Tag>;
                }
                return (
                  <Tooltip title={`Details: ${pricingStatus.details.join(', ')}`}>
                    <Tag color={pricingStatus.color} icon={<DollarOutlined />}>
                      {pricingStatus.label}
                    </Tag>
                  </Tooltip>
                );
              }}
            />
            <Table.Column
              title="Aufgaben-Status"
              width={130}
              render={(_, record: IAdvert) => {
                const taskStatus = record.overview_data?.task_status;
                if (!taskStatus || taskStatus.total_tasks === 0) {
                  return <Tag color="default" icon={<ClockCircleOutlined />}>Keine Aufgaben</Tag>;
                }
                
                const getStatusColor = (status: string) => {
                  switch (status) {
                    case 'complete': return 'green';
                    case 'medium': return 'orange';
                    case 'low': return 'red';
                    default: return 'default';
                  }
                };

                const getStatusIcon = (status: string) => {
                  switch (status) {
                    case 'complete': return <CheckSquareOutlined />;
                    case 'medium': return <ClockCircleOutlined />;
                    case 'low': return <ExclamationCircleOutlined />;
                    default: return <ClockCircleOutlined />;
                  }
                };

                return (
                  <Tooltip title={`${taskStatus.completed_tasks}/${taskStatus.total_tasks} Aufgaben erledigt (${taskStatus.completion_rate}%)`}>
                    <Tag 
                      color={getStatusColor(taskStatus.status)} 
                      icon={getStatusIcon(taskStatus.status)}
                    >
                      {taskStatus.label}
                    </Tag>
                  </Tooltip>
                );
              }}
            />
            <Table.Column
              title="Aktionen"
              width={150}
              render={(_, record: IAdvert) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => handleAdvertDetail(record)}
                    title="Details anzeigen"
                  >
                    Details
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => message.info("Löschen von Inseraten wird implementiert...")}
                    title="Löschen"
                  />
                </Space>
              )}
            />
          </Table>
        </TabPane>

        {/* Tab für Eigentümer */}
        <TabPane 
          tab={
            <span>
              <UserOutlined />
              Eigentümer
            </span>
          } 
          key="senders"
        >
          <Table {...tableProps} rowKey="id">
            <Table.Column
              dataIndex="type"
              title="Typ"
              width={100}
              render={(value, record: ISender) => (
                <Tag color={value === 'advert' ? 'blue' : 'green'} icon={<UserOutlined />}>
                  {value === 'advert' ? 'Inserat' : 'Benutzer'}
                </Tag>
              )}
            />
            <Table.Column
              dataIndex="name"
              title="Name/Firma"
              sorter
              render={(value, record: ISender) => (
                <Space direction="vertical" size={0}>
                  <strong>{record.company || record.name}</strong>
                  {record.shortname && (
                    <small style={{ color: '#999' }}>{record.shortname}</small>
                  )}
                </Space>
              )}
            />
            <Table.Column
              dataIndex="address"
              title="Adresse"
              render={(value, record: ISender) => (
                <Space direction="vertical" size={0}>
                  <span>{value}</span>
                  <small style={{ color: '#999' }}>
                    {record.zip} {record.city}
                  </small>
                </Space>
              )}
            />
            <Table.Column
              dataIndex="isOwner"
              title="Berechtigung"
              width={120}
              render={(value) => (
                <Tag color={value ? 'green' : 'orange'}>
                  {value ? 'Eigentümer' : 'Fremd'}
                </Tag>
              )}
            />
            <Table.Column
              title="Aktionen"
              dataIndex="actions"
              width={120}
              render={(_, record: ISender) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    title="Bearbeiten"
                  />
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                    title="Löschen"
                    disabled={record.isOwner} // Eigene Absender nicht löschbar
                  />
                </Space>
              )}
            />
          </Table>
        </TabPane>
      </Tabs>
    </List>
  );
}; 